import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  parseFiniteCoordinate,
  quantizeGpsPairToNearestMeter,
} from '../utils/gpsPrecision';

const STORAGE_KEY = 'exiftrator-locations-v1';

const LocationsContext = createContext();

function normalizeLocationCoordinates(location) {
  const latitude = parseFiniteCoordinate(location?.latitude);
  const longitude = parseFiniteCoordinate(location?.longitude);

  if (latitude === null && longitude === null) {
    return location;
  }

  const quantized = quantizeGpsPairToNearestMeter(latitude, longitude);

  return {
    ...location,
    ...(latitude !== null ? { latitude: quantized.latitude } : {}),
    ...(longitude !== null ? { longitude: quantized.longitude } : {}),
  };
}

/**
 * Location structure: { id, name, latitude, longitude, address? }
 */
export function LocationsProvider({ children }) {
  const [locations, setLocations] = useState([]);

  // Load locations from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const normalized = Array.isArray(parsed)
          ? parsed.map(normalizeLocationCoordinates)
          : [];

        setLocations(normalized);

        if (JSON.stringify(parsed) !== JSON.stringify(normalized)) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
        }
      }
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  }, []);

  // Save to localStorage
  const saveToStorage = useCallback((newLocations) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newLocations));
    } catch (error) {
      console.error('Failed to save locations:', error);
    }
  }, []);

  // Add a new location
  const addLocation = useCallback((name, latitude, longitude, address = null) => {
    // Check for duplicate name (case-insensitive)
    const duplicate = locations.find(l => l.name.toLowerCase() === name.toLowerCase());
    if (duplicate) {
      return { error: `Location "${name}" already exists` };
    }

    const parsedLatitude = parseFiniteCoordinate(latitude);
    const parsedLongitude = parseFiniteCoordinate(longitude);
    const quantized = quantizeGpsPairToNearestMeter(parsedLatitude, parsedLongitude);

    const newLocation = {
      id: Date.now(),
      name,
      latitude: quantized.latitude,
      longitude: quantized.longitude,
      address
    };

    setLocations(prev => {
      const updated = [...prev, newLocation];
      saveToStorage(updated);
      return updated;
    });

    return { location: newLocation };
  }, [saveToStorage, locations]);

  // Update a location
  const updateLocation = useCallback((locationId, updates) => {
    setLocations(prev => {
      const updated = prev.map(l =>
        l.id === locationId
          ? normalizeLocationCoordinates({ ...l, ...updates })
          : l
      );
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  // Delete a location
  const deleteLocation = useCallback((locationId) => {
    setLocations(prev => {
      const updated = prev.filter(l => l.id !== locationId);
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  // Get all locations
  const getAllLocations = useCallback(() => {
    return locations;
  }, [locations]);

  // Get a location by ID
  const getLocationById = useCallback((locationId) => {
    return locations.find(l => l.id === locationId);
  }, [locations]);

  // Check if a location name already exists (case-insensitive)
  const locationNameExists = useCallback((name) => {
    return locations.some(l => l.name.toLowerCase() === name.toLowerCase());
  }, [locations]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    locations,
    getAllLocations,
    getLocationById,
    locationNameExists,
    addLocation,
    updateLocation,
    deleteLocation
  }), [
    locations,
    getAllLocations,
    getLocationById,
    locationNameExists,
    addLocation,
    updateLocation,
    deleteLocation
  ]);

  return (
    <LocationsContext.Provider value={value}>
      {children}
    </LocationsContext.Provider>
  );
}

export function useLocations() {
  const context = useContext(LocationsContext);
  if (!context) {
    throw new Error('useLocations must be used within LocationsProvider');
  }
  return context;
}
